import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useMechanicSkills } from '@/hooks/useSkills';
import {
  useUpsertSkillCell,
  type SkillMatrixCell,
  type UpsertSkillCellBody,
} from '@/hooks/useSkillMatrix';
import { useNotify } from '@/hooks/useNotify';
import { api } from '@/lib/api';
import type { Attachment } from '@/types';
import type { CanonicalLevel } from '@/utils/skillLevels';

const CERT_ENTITY = 'user_skill_certificate';

interface CertificateDialogProps {
  open: boolean;
  userId: string;
  skillId: string;
  skillName: string;
  cell: SkillMatrixCell | undefined;
  onClose: () => void;
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function toIso(value: string): string | null {
  if (!value) return null;
  return `${value}T00:00:00.000Z`;
}

export function CertificateDialog({
  open,
  userId,
  skillId,
  skillName,
  cell,
  onClose,
}: CertificateDialogProps) {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const upsert = useUpsertSkillCell();
  const skillsQuery = useMechanicSkills(open ? userId : undefined);

  const [issuer, setIssuer] = useState('');
  const [number, setNumber] = useState('');
  const [certifiedDate, setCertifiedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [assignmentId, setAssignmentId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIssuer(cell?.certificate.issuer ?? '');
    setNumber(cell?.certificate.number ?? '');
    setCertifiedDate(toDateInput(cell?.certificate.certifiedAt));
    setExpiryDate(toDateInput(cell?.certificate.expiresAt));
    setAssignmentId(cell?.assignmentId ?? null);
  }, [open, cell]);

  useEffect(() => {
    if (!open) return;
    const match = skillsQuery.data?.find((row) => row.skillId === skillId);
    if (match) setAssignmentId(match.id);
  }, [open, skillId, skillsQuery.data]);

  useEffect(() => {
    if (!open || !assignmentId) {
      setAttachments([]);
      return;
    }
    const entities = JSON.stringify([{ entityType: CERT_ENTITY, entityId: assignmentId }]);
    let cancelled = false;
    void api
      .get<{ data: Attachment[] }>(`/attachments/by-entities?entities=${encodeURIComponent(entities)}`)
      .then((result) => {
        if (!cancelled) setAttachments(result.data);
      })
      .catch(() => {
        if (!cancelled) setAttachments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assignmentId]);

  const ensureAssignment = async (): Promise<string | null> => {
    if (assignmentId) return assignmentId;
    const level: CanonicalLevel | null = cell?.level ?? null;
    const wantsToLearn = cell?.wantsToLearn ?? level === null;
    const body: UpsertSkillCellBody = {
      level,
      wantsToLearn: wantsToLearn || level === null,
    };
    const result = await upsert.mutateAsync({
      userId,
      skillId,
      ...(cell?.updatedAt ? { updatedAt: cell.updatedAt } : {}),
      body,
    });
    if (!result) return null;
    setAssignmentId(result.id);
    return result.id;
  };

  const handleSave = async () => {
    try {
      const body: UpsertSkillCellBody = {
        level: cell?.level ?? null,
        wantsToLearn: cell?.wantsToLearn ?? true,
        certificateIssuer: issuer.trim() ? issuer.trim() : null,
        certificateNumber: number.trim() ? number.trim() : null,
        certifiedAt: toIso(certifiedDate),
        expiresAt: toIso(expiryDate),
      };
      await upsert.mutateAsync({
        userId,
        skillId,
        ...(cell?.updatedAt ? { updatedAt: cell.updatedAt } : {}),
        body,
      });
      notify.success(t('skillsMatrix.certificateSaved'));
      onClose();
    } catch {
      notify.error(t('skillsMatrix.certificateSaveFailed'));
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const id = await ensureAssignment();
      if (!id) throw new Error('missing assignment');
      const entityLinks = [{ entityType: CERT_ENTITY, entityId: id }];
      const { uploadUrl, fileKey, fileUrl } = await api.post<{
        uploadUrl: string;
        fileKey: string;
        fileUrl: string;
      }>('/attachments/upload-url', {
        mimeType: file.type,
        fileName: file.name,
        fileSizeBytes: file.size,
        entityLinks,
      });
      const uploaded = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!uploaded.ok) throw new Error('upload failed');
      const attachment = await api.post<Attachment>('/attachments', {
        fileKey,
        fileUrl,
        originalFileName: file.name,
        mimeType: file.type,
        fileSizeBytes: file.size,
        entityLinks,
      });
      setAttachments((prev) => [...prev, attachment]);
    } catch {
      notify.error(t('skillsMatrix.certificateUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (id: string) => {
    try {
      await api.delete(`/attachments/${id}`);
      setAttachments((prev) => prev.filter((item) => item.id !== id));
    } catch {
      notify.error(t('skillsMatrix.certificateUploadFailed'));
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('skillsMatrix.certificateTitle', { skill: skillName })}
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle1">
            {t('skillsMatrix.certificateTitle', { skill: skillName })}
          </Typography>
          <TextField
            label={t('skillsMatrix.certificateIssuer')}
            value={issuer}
            onChange={(event) => setIssuer(event.target.value)}
            fullWidth
          />
          <TextField
            label={t('skillsMatrix.certificateNumber')}
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            fullWidth
          />
          <TextField
            label={t('skillsMatrix.certifiedAt')}
            type="date"
            value={certifiedDate}
            onChange={(event) => setCertifiedDate(event.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('skillsMatrix.expiresAt')}
            type="date"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
            >
              {t('skillsMatrix.uploadAttachment')}
              <input
                type="file"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleUpload(file);
                  event.target.value = '';
                }}
              />
            </Button>
          </Stack>
          <List dense>
            {attachments.map((attachment) => (
              <ListItem
                key={attachment.id}
                secondaryAction={
                  <Tooltip title={tc('delete')}>
                    <IconButton
                      edge="end"
                      aria-label={tc('delete')}
                      onClick={() => void handleDeleteAttachment(attachment.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText primary={attachment.originalFileName} />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button onClick={() => void handleSave()} variant="contained" disabled={upsert.isPending}>
          {tc('save')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
